import Skeleton from 'react-loading-skeleton';

import { CalendarIcon } from '@radix-ui/react-icons';
import { Flex, Select, Text } from '@raystack/apsara';
import _ from 'lodash';
import moment from 'moment';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import { ScrollView } from '@src/components/PixxelUIKit/ScrollView';
import { For, Show } from '@src/hoc';
import { getDateRange } from '@src/utils/commonUtils';
import { ImageLinks } from '@src/utils/constants/MODE_INFERENCE_DEFAULTS';
import { getUSTimeFormat } from '@src/utils/dateUtils';

import { StrokeLine } from './CropPhenologyCommon';
import { InferenceAssetItem } from './InferenceAssetItem';

import styles from './styles.module.css';

const getNdviValues = arr =>
  arr?.split(',')?.map(d => Number(d.trim()) || null);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const { payload: data } = payload[0];
    return (
      <Flex
        direction='column'
        gap='small'
        align='start'
        className={styles.graphTooltipWrapper}
      >
        <Text size={2} weight={500}>
          {label}
        </Text>
        <Flex gap='medium' align='stretch'>
          <Text size={2} weight={500} variant='secondary'>
            Mean NDVI
          </Text>
          <Text size={2} weight={500}>
            : {data.ndvi.toFixed(3)}
          </Text>
        </Flex>
      </Flex>
    );
  }

  return null;
};

const GapFillNdviGraph = ({ activeFarm = {} }) => {
  const { properties = {} } = activeFarm;
  const dates = properties.timestamps
    ?.split(',')
    ?.map(d => moment(d.trim()).format('D MMM YY'));
  const gapNdvis = getNdviValues(properties.gap_filled_ndvi);
  const meanNdvis = getNdviValues(properties.mean_ndvi);

  const data = dates.map((d, i) => ({
    id: i,
    date: d,
    ndvi: meanNdvis[i] || gapNdvis[i],
    color: meanNdvis[i]
      ? 'var(--rs-color-foreground-danger-primary)'
      : 'var(--rs-color-foreground-accent-primary)'
  }));

  const renderCustomDot = props => {
    const { cx, cy, payload: { color = '' } = {} } = props;
    return <circle cx={cx} cy={cy} r='3' fill={color} />;
  };

  return (
    <Flex direction='column' gap='small'>
      <AreaChart
        width={400}
        height={300}
        data={data || []}
        margin={{
          top: 0,
          right: 18,
          left: -10
        }}
      >
        <XAxis dataKey='date' fontSize={11} />
        <YAxis
          domain={[0, 1]}
          fontSize={12}
          dataKey='ndvi'
          label={{
            value: 'Mean NDVI Values',
            style: { textAnchor: 'middle', fontSize: '11px' },
            angle: -90,
            position: 'left',
            offset: -18
          }}
        />
        <CartesianGrid
          strokeDasharray='3 3'
          stroke='var(--rs-color-foreground-base-secondary)'
          vertical={false}
          opacity={0.4}
        />
        <Tooltip content={<CustomTooltip />} />
        <defs>
          <linearGradient id='lineargradient_graph' x1='0' y1='0' x2='0' y2='1'>
            <stop stopColor='#FFE8E8' stopOpacity={1} />
            <stop offset='1' stopColor='#FBFCFD' stopOpacity={1} />
          </linearGradient>
        </defs>
        <Area
          type='monotone'
          dataKey='ndvi'
          stroke='#C1C8CD'
          strokeWidth={1}
          dot={renderCustomDot}
          activeDot={false}
          fill='url(#lineargradient_graph)'
        />
      </AreaChart>
      <Flex
        justify='between'
        align='start'
        style={{
          flexFlow: 'row wrap'
        }}
      >
        <Flex gap='small' align='center'>
          <StrokeLine color={'var(--rs-color-foreground-danger-primary)'} />
          <Text size={1} variant='secondary'>
            Sentinel-2 NDVI values
          </Text>
        </Flex>
        <Flex gap='small' align='center'>
          <StrokeLine color={'var(--rs-color-foreground-accent-primary)'} />
          <Text size={1} variant='secondary'>
            Sentinel-1 (Estimated) NDVI values
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
};

const NDVIGraphSection = ({ activeFarm = {}, farms = [], onValueChange }) => {
  return (
    <>
      <div className={styles.headingDiv2}>
        <Text className={styles.title} size={4}>
          NDVI graph
        </Text>
        <Select value={activeFarm.id} onValueChange={onValueChange}>
          <Select.Trigger className={styles.paramSelect}>
            <Select.Value placeholder='Select a farm' />
          </Select.Trigger>
          <Select.Content>
            <Select.Group>
              <ScrollView height='200px'>
                <For each={farms || []}>
                  {d => (
                    <Select.Item value={d.id} key={d.id}>
                      <Flex
                        gap='extra-small'
                        align='center'
                        justify='start'
                        wrap='noWrap'
                      >
                        <Text size={2}>Farm {d.id} </Text>
                        <Text size={2} variant='tertiary'>
                          ({d.area} m<sup>2</sup>)
                        </Text>
                      </Flex>
                    </Select.Item>
                  )}
                </For>
              </ScrollView>
            </Select.Group>
          </Select.Content>
        </Select>
      </div>

      <Show when={!_.isEmpty(activeFarm)}>
        <GapFillNdviGraph activeFarm={activeFarm} />
      </Show>
    </>
  );
};

NDVIGraphSection.displayName = NDVIGraphSection;

const GapFillNdviCommon = ({
  assetType,
  assetName,
  assetDate,
  inputs,
  loader,
  activeFarm,
  farms,
  onChangeActiveFarm
}) => {
  return (
    <>
      <Show when={assetType && assetName && assetDate}>
        <Flex direction='column' gap='small'>
          <Text size={4} weight={500}>
            Farm boundary
          </Text>
          <InferenceAssetItem src={ImageLinks[assetType]} name={assetName}>
            <Text size={2} variant='secondary'>
              {getUSTimeFormat(assetDate)}
            </Text>
          </InferenceAssetItem>
        </Flex>
      </Show>

      <Flex direction='column' gap='small'>
        <Text size={4} weight={500}>
          Timeframe
        </Text>
        <Flex gap='small' align='center'>
          <CalendarIcon />
          <Text size={4}>
            {getDateRange(
              inputs?.find(d => d.name === 'start_date')?.value,
              inputs?.find(d => d.name === 'end_date')?.value
            )}
          </Text>
        </Flex>
      </Flex>
      <Show
        when={!loader}
        fallback={<Skeleton count={4} height={40} width={400} />}
      >
        <NDVIGraphSection
          activeFarm={activeFarm}
          farms={farms}
          onValueChange={onChangeActiveFarm}
        />
      </Show>
    </>
  );
};

export default GapFillNdviCommon;
